import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Clock3,
  Coins,
  Copy,
  Flag,
  Mail,
  MessageCircle,
  Mic,
  MicOff,
  Pin,
  Send,
  Share2,
  Smile,
  Users,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { toast } from "sonner";
import LiveViewerRoster from "@/components/LiveViewerRoster";

const glass = "border border-white/10 bg-[#141418]/65 shadow-2xl backdrop-blur-xl";

const initials = (name) => String(name || "Creator").trim().slice(0, 1).toUpperCase() || "C";

const formatElapsedTime = (seconds) => {
  const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
};

export const readUsableLiveViewportDimensions = () => {
  if (typeof window === "undefined") return { width: 1280, height: 720 };

  const heights = [
    window.visualViewport?.height,
    window.innerHeight,
    document.documentElement?.clientHeight,
  ].filter((value) => Number.isFinite(value) && value > 0);
  const widths = [
    window.visualViewport?.width,
    window.innerWidth,
    document.documentElement?.clientWidth,
  ].filter((value) => Number.isFinite(value) && value > 0);

  return {
    width: Math.max(Math.round(Math.min(...widths)), 320),
    height: Math.max(Math.round(Math.min(...heights)), 320),
  };
};

export const LIVE_VIEWPORT_ROTATION_SETTLE_MS = 450;

export const readLiveViewportOrientation = () => {
  if (typeof window === "undefined") return "landscape";
  if (window.matchMedia) {
    return window.matchMedia("(orientation: landscape)").matches ? "landscape" : "portrait";
  }
  return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
};

const normalizeLiveViewportDimensions = (dimensions) => {
  const width = Number(dimensions?.width);
  const height = Number(dimensions?.height);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }
  return {
    width: Math.max(Math.round(width), 320),
    height: Math.max(Math.round(height), 320),
  };
};

const sameLiveViewportDimensions = (left, right) => (
  left?.width === right?.width && left?.height === right?.height
);

// One cross-browser rule for every live surface:
// - capture exact CSS-pixel dimensions when the live experience activates;
// - ignore Window/VisualViewport resize events from browser chrome, keyboards,
//   pinch zoom, and focus changes;
// - recapture only after a real orientation signal has remained stable.
// Camera tracks never participate in this hook, so layout changes cannot
// restart or resize the published video.
export const useLiveViewportLock = ({
  active = true,
  initialDimensions = null,
  allowRotation = true,
  rotationSettleMs = LIVE_VIEWPORT_ROTATION_SETTLE_MS,
} = {}) => {
  const initialWidth = Number(initialDimensions?.width);
  const initialHeight = Number(initialDimensions?.height);
  const [dimensions, setDimensions] = useState(() => (
    normalizeLiveViewportDimensions(initialDimensions) || readUsableLiveViewportDimensions()
  ));
  const orientationRef = useRef(readLiveViewportOrientation());

  useEffect(() => {
    if (!active) return undefined;

    const requestedDimensions = normalizeLiveViewportDimensions({
      width: initialWidth,
      height: initialHeight,
    });
    const capturedDimensions = requestedDimensions || readUsableLiveViewportDimensions();
    orientationRef.current = readLiveViewportOrientation();
    setDimensions((current) => (
      sameLiveViewportDimensions(current, capturedDimensions) ? current : capturedDimensions
    ));

    if (!allowRotation) return undefined;

    const orientationMedia = window.matchMedia?.("(orientation: landscape)");
    const screenOrientation = window.screen?.orientation;
    let rotationTimer = null;

    const settleRealRotation = () => {
      window.clearTimeout(rotationTimer);
      rotationTimer = window.setTimeout(() => {
        const settledOrientation = readLiveViewportOrientation();
        if (settledOrientation === orientationRef.current) return;

        orientationRef.current = settledOrientation;
        const rotatedDimensions = readUsableLiveViewportDimensions();
        setDimensions((current) => (
          sameLiveViewportDimensions(current, rotatedDimensions) ? current : rotatedDimensions
        ));
      }, Math.max(Number(rotationSettleMs) || 0, 0));
    };

    if (orientationMedia?.addEventListener) {
      orientationMedia.addEventListener("change", settleRealRotation);
    } else {
      orientationMedia?.addListener?.(settleRealRotation);
    }
    screenOrientation?.addEventListener?.("change", settleRealRotation);
    window.addEventListener("orientationchange", settleRealRotation);

    return () => {
      window.clearTimeout(rotationTimer);
      if (orientationMedia?.removeEventListener) {
        orientationMedia.removeEventListener("change", settleRealRotation);
      } else {
        orientationMedia?.removeListener?.(settleRealRotation);
      }
      screenOrientation?.removeEventListener?.("change", settleRealRotation);
      window.removeEventListener("orientationchange", settleRealRotation);
    };
  }, [active, allowRotation, initialHeight, initialWidth, rotationSettleMs]);

  return dimensions;
};

// Backwards-compatible height-only adapter for any non-stage callers.
export const useLockedLiveViewportHeight = (frozenHeight = null) => {
  const currentDimensions = readUsableLiveViewportDimensions();
  return useLiveViewportLock({
    initialDimensions: Number.isFinite(Number(frozenHeight)) && Number(frozenHeight) > 0
      ? { width: currentDimensions.width, height: Number(frozenHeight) }
      : null,
    allowRotation: frozenHeight == null,
  }).height;
};

const audienceSpeakerLabel = (mode) => (
  mode === "podcast" ? "Call-In" : mode === "religion" ? "Mic" : "Raise Hand"
);

const audienceSpeakerIcon = (mode) => (
  mode === "podcast" ? "☎️" : mode === "religion" ? "🎙️" : "🖐️"
);

export const LiveTopBar = ({
  eventId,
  creatorName,
  creatorPicture,
  eventTitle,
  viewerCount = 0,
  streamTime = 0,
  isCreator = false,
  isLive = true,
  onReport,
  onExit,
  audienceSpeakerMode = null,
  audienceSpeakerEnabled = false,
  onToggleAudienceSpeaker,
  audienceSpeakerBusy = false,
}) => (
  <div
    className="pointer-events-none absolute inset-x-0 top-0 z-50 flex items-start gap-2 px-3 pt-[max(env(safe-area-inset-top),0.75rem)] sm:px-4"
    data-testid={isCreator ? "creator-live-top-bar" : "viewer-live-top-bar"}
  >
    <div className={`pointer-events-auto flex min-w-0 items-center gap-2 rounded-full py-1.5 pl-1.5 pr-3 ${glass}`}>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-red-400 bg-gradient-to-br from-red-600 to-red-950 text-sm font-black text-white">
        {creatorPicture ? <img src={creatorPicture} alt="" className="h-full w-full object-cover" /> : initials(creatorName)}
      </div>
      <div className="min-w-0 leading-tight">
        <div className="flex items-center gap-1 text-[13px] font-extrabold text-white">
          <span className="max-w-[8rem] truncate sm:max-w-[14rem]">{creatorName || "Creator"}</span>
          <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-rose-600 text-white">
            <Check className="h-2.5 w-2.5" strokeWidth={3} />
          </span>
        </div>
        <p className="max-w-[10rem] truncate text-[10px] text-gray-300 sm:max-w-[18rem]">{eventTitle || "Live on ShowMeLive"}</p>
      </div>
    </div>

    <div className="flex-1" />

    {isLive && (
      <div className="pointer-events-none flex h-9 items-center gap-1.5 rounded-full bg-rose-600/95 px-3 text-xs font-black tracking-[0.08em] text-white shadow-lg shadow-rose-950/40">
        <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
        LIVE
      </div>
    )}

    <div className="flex shrink-0 flex-col items-center gap-1">
      <LiveViewerRoster
        eventId={eventId}
        viewerCount={viewerCount}
        isCreator={isCreator}
        buttonClassName={`h-9 rounded-full px-3 text-sm font-bold text-white ${glass}`}
        testId={isCreator ? "creator-live-viewer-count" : "viewer-live-viewer-count"}
      />
      {isCreator && isLive && audienceSpeakerMode && onToggleAudienceSpeaker && (
        <button
          type="button"
          onClick={onToggleAudienceSpeaker}
          disabled={audienceSpeakerBusy}
          className={`pointer-events-auto flex h-7 min-w-[3.5rem] items-center justify-center gap-1 rounded-full px-2 text-[9px] font-black text-white transition active:scale-95 disabled:cursor-wait disabled:opacity-60 ${glass} ${
            audienceSpeakerEnabled ? "border-emerald-400/50 bg-emerald-500/25" : "border-white/15 bg-black/45 text-gray-300"
          }`}
          aria-label={`${audienceSpeakerEnabled ? "Turn off" : "Turn on"} ${audienceSpeakerLabel(audienceSpeakerMode)}`}
          title={`${audienceSpeakerLabel(audienceSpeakerMode)} ${audienceSpeakerEnabled ? "on" : "off"}`}
          data-testid="creator-audience-speaker-toggle"
        >
          <span aria-hidden="true">{audienceSpeakerIcon(audienceSpeakerMode)}</span>
          <span>{audienceSpeakerEnabled ? "ON" : "OFF"}</span>
        </button>
      )}
      {!isCreator && isLive && (
        <div
          className={`flex h-7 items-center gap-1 rounded-full px-2.5 font-mono text-[10px] font-bold text-white ${glass}`}
          title="Live stream timer"
          data-testid={isCreator ? "creator-stream-timer" : "viewer-stream-timer"}
        >
          <Clock3 className="h-3 w-3 text-blue-300" />
          <span>{formatElapsedTime(streamTime)}</span>
        </div>
      )}
    </div>

    {onReport && !isCreator && (
      <button
        type="button"
        onClick={onReport}
        className={`pointer-events-auto absolute right-3 flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-bold text-red-200 transition hover:bg-red-500/20 sm:right-4 ${glass}`}
        style={{ top: "calc(max(env(safe-area-inset-top), 0.75rem) + 5rem)" }}
        aria-label="Report live stream"
        title="Report live stream"
        data-testid="viewer-report-btn"
      >
        <Flag className="h-4 w-4" /> Report
      </button>
    )}

    {onExit && (
      <div className="flex shrink-0 flex-col items-center gap-1">
        <button
          type="button"
          onClick={onExit}
          className={`pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition hover:bg-white/15 ${glass}`}
          aria-label={isCreator ? "Exit to Creator Studio" : "Exit live event"}
          data-testid={isCreator ? "creator-exit-btn" : "exit-btn"}
        >
          <X className="h-4 w-4" />
        </button>
        {isCreator && isLive && (
          <div
            className={`flex h-7 items-center gap-1 rounded-full px-2 font-mono text-[9px] font-bold text-white ${glass}`}
            title="Live stream timer"
            data-testid="creator-stream-timer"
          >
            <Clock3 className="h-3 w-3 text-blue-300" />
            <span>{formatElapsedTime(streamTime)}</span>
          </div>
        )}
      </div>
    )}
  </div>
);

export const CreatorTitheBadge = ({
  total = 0,
  label = "Tithes",
}) => (
  <div
    className="absolute left-3 top-[calc(max(env(safe-area-inset-top),0.75rem)+3.5rem)] z-40 rounded-2xl border border-yellow-300/35 bg-yellow-400/15 px-3 py-1.5 text-xs shadow-xl backdrop-blur-xl sm:left-4"
    data-testid="creator-tithe-panel"
  >
    <div className="flex items-center gap-1.5">
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-yellow-100 via-yellow-400 to-amber-700 text-[9px] font-black text-amber-950">$</span>
      <span className="font-extrabold text-yellow-200">{label}</span>
      <span className="font-extrabold text-white">${Number(total || 0).toFixed(2)}</span>
    </div>
  </div>
);

export const CREATOR_TIP_NOTIFICATION_MS = 15_000;

export const useCreatorTipNotifications = (
  durationMs = CREATOR_TIP_NOTIFICATION_MS,
) => {
  const [notifications, setNotifications] = useState([]);
  const seenIdsRef = useRef(new Set());
  const timersRef = useRef(new Map());

  const enqueueTipNotification = useCallback(({
    id,
    senderName,
    amount,
    receivedAt = Date.now(),
  }) => {
    if (!senderName) return;
    const safeId = String(id || `${Date.now()}-${Math.random()}`);
    if (seenIdsRef.current.has(safeId)) return;
    seenIdsRef.current.add(safeId);

    const numericReceivedAt = Number(receivedAt);
    const safeReceivedAt = Number.isFinite(numericReceivedAt)
      ? numericReceivedAt
      : Date.now();
    const expiresAt = safeReceivedAt + durationMs;
    const remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0) return;

    setNotifications((current) => [
      ...current,
      {
        id: safeId,
        senderName: String(senderName),
        amount: Number(amount || 0),
        expiresAt,
      },
    ].slice(-20));

    const timer = window.setTimeout(() => {
      timersRef.current.delete(safeId);
      setNotifications((current) => current.filter(
        (notification) => notification.id !== safeId,
      ));
    }, remainingMs);
    timersRef.current.set(safeId, timer);
  }, [durationMs]);

  useEffect(() => () => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  return { notifications, enqueueTipNotification };
};

export const CreatorTipNotificationStack = ({
  notifications = [],
  className = "",
}) => {
  if (!notifications.length) return null;

  return (
    <div
      className={`pointer-events-none absolute left-3 top-[calc(max(env(safe-area-inset-top),0.75rem)+6.75rem)] z-50 flex max-h-[min(18dvh,7rem)] w-[min(10.25rem,calc(100%-1.5rem))] flex-col gap-1 overflow-hidden sm:left-4 ${className}`}
      data-testid="creator-tip-notification-stack"
      aria-live="polite"
    >
      {[...notifications].reverse().map((notification) => (
        <div
          key={notification.id}
          className="animate-in fade-in slide-in-from-left-2 rounded-lg border border-green-300/25 bg-[#0c1811]/88 px-2.5 py-1.5 shadow-lg backdrop-blur-lg"
          data-testid="creator-tip-notification"
        >
          <p className="truncate text-[11px] font-extrabold leading-4 text-green-100">
            {notification.senderName} sent ${Number(notification.amount || 0).toFixed(2)}
          </p>
        </div>
      ))}
    </div>
  );
};

export const SHOWME_CONGREGATION_IDLE_MS = 10_000;
export const CHAT_MODERATION_SWIPE_THRESHOLD_PX = 44;

export const getChatModerationSwipeIntent = ({ startX, startY, endX, endY }) => {
  const deltaX = Number(endX) - Number(startX);
  const deltaY = Number(endY) - Number(startY);

  if (!Number.isFinite(deltaX) || !Number.isFinite(deltaY)) return null;
  if (Math.abs(deltaX) < CHAT_MODERATION_SWIPE_THRESHOLD_PX) return null;
  if (Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return null;
  return deltaX < 0 ? "reveal" : "close";
};

export const SwipeChatModerationRow = ({
  children,
  messageId,
  username,
  isMuted,
  canModerate,
  isRevealed,
  onReveal,
  onClose,
  onModerate,
  className = "",
}) => {
  const swipeStartRef = useRef(null);
  const actionLabel = isMuted ? "Unmute" : "Mute";

  const beginSwipe = (event) => {
    if (!canModerate || (event.button != null && event.button !== 0)) return;
    if (event.target.closest("button, a, input, textarea, select")) return;
    swipeStartRef.current = { x: event.clientX, y: event.clientY };
  };

  const finishSwipe = (event) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!canModerate || !start) return;
    const intent = getChatModerationSwipeIntent({
      startX: start.x,
      startY: start.y,
      endX: event.clientX,
      endY: event.clientY,
    });
    if (intent === "reveal") onReveal?.(messageId);
    if (intent === "close") onClose?.();
  };

  if (!canModerate) return children;

  return (
    <div
      className={`relative shrink-0 overflow-hidden rounded-xl ${className}`}
      data-testid={`creator-chat-swipe-row-${messageId}`}
      data-swipe-state={isRevealed ? "revealed" : "closed"}
    >
      {isRevealed && (
        <button
          type="button"
          onClick={() => {
            onModerate?.();
            onClose?.();
          }}
          className={`absolute inset-y-0 right-0 flex w-[4.75rem] items-center justify-center text-[10px] font-black text-white shadow-lg transition-colors ${
            isMuted ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"
          }`}
          aria-label={`${actionLabel} ${username} for this event`}
          data-testid={`creator-swipe-${isMuted ? "unmute" : "mute"}-message-${messageId}`}
        >
          {isMuted ? <Volume2 className="mr-1 h-3.5 w-3.5" /> : <VolumeX className="mr-1 h-3.5 w-3.5" />}
          {actionLabel}
        </button>
      )}
      <div
        className="relative touch-pan-y rounded-xl bg-transparent px-1.5 py-1 transition-transform duration-200 ease-out"
        style={{ transform: isRevealed ? "translateX(-4.75rem)" : "translateX(0)" }}
        onPointerDown={beginSwipe}
        onPointerUp={finishSwipe}
        onPointerCancel={() => { swipeStartRef.current = null; }}
        onClick={() => isRevealed && onClose?.()}
        aria-label={`Chat message from ${username}. Swipe left for ${actionLabel.toLowerCase()}.`}
        data-testid={`creator-chat-message-surface-${messageId}`}
      >
        {children}
      </div>
    </div>
  );
};

export const LiveChatLane = ({
  messages = [],
  connected = true,
  audience = "viewer",
  aboveChatControl = null,
  onPinMessage,
  onMuteUser,
  onUnmuteUser,
  mutedUserIds = [],
  isShowMeCongregation = false,
  conversationLabel = "",
}) => {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef(null);
  const historyRef = useRef(null);
  const stickToLatestRef = useRef(true);
  const [revealedMuteId, setRevealedMuteId] = useState(null);
  const mutedSet = useMemo(() => new Set(mutedUserIds.map(String)), [mutedUserIds]);
  const visibleMessages = useMemo(
    () => messages.filter((message) => !mutedSet.has(String(message.user_id || ""))),
    [messages, mutedSet],
  );
  const latestId = visibleMessages[visibleMessages.length - 1]?.id;
  const keepActive = useCallback(() => {
    setIsIdle(false);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(
      () => setIsIdle(true),
      isShowMeCongregation ? SHOWME_CONGREGATION_IDLE_MS : 60_000,
    );
  }, [isShowMeCongregation]);

  useEffect(() => {
    keepActive();
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [keepActive]);

  useEffect(() => {
    // Every newly delivered message must wake the conversation immediately.
    // The socket already updates `messages`; this keeps the idle presentation
    // from waiting for a local screen touch before React reveals that update.
    if (!latestId) return;
    keepActive();
  }, [keepActive, latestId]);

  useEffect(() => {
    if (!isShowMeCongregation) return undefined;
    const revealOnLocalTap = () => keepActive();
    document.addEventListener("pointerdown", revealOnLocalTap, { passive: true });
    return () => document.removeEventListener("pointerdown", revealOnLocalTap);
  }, [isShowMeCongregation, keepActive]);

  const { pinnedMessage, historyMessages } = useMemo(() => {
    const pinned = [...visibleMessages].reverse().find((message) => message.is_pinned) || null;
    return {
      pinnedMessage: pinned,
      historyMessages: visibleMessages.filter((message) => !pinned || String(message.id) !== String(pinned.id)),
    };
  }, [visibleMessages]);

  useEffect(() => {
    const history = historyRef.current;
    if (!history || !stickToLatestRef.current) return;
    if (typeof history.scrollTo === "function") {
      history.scrollTo({ top: history.scrollHeight, behavior: "smooth" });
    } else {
      history.scrollTop = history.scrollHeight;
    }
  }, [latestId]);

  const handleHistoryScroll = useCallback(() => {
    keepActive();
    const history = historyRef.current;
    if (!history) return;
    const distanceFromLatest = history.scrollHeight - history.scrollTop - history.clientHeight;
    stickToLatestRef.current = distanceFromLatest <= 40;
  }, [keepActive]);

  if (!visibleMessages.length && !aboveChatControl) return null;

  const renderMessage = (message, pinned = false) => {
    const username = message.username || message.user_name || "Viewer";
    const body = message.message || message.text || "";
    const isChatMuted = Boolean(message.is_chat_muted);
    const canMute = Boolean(onMuteUser && message.user_id && !isChatMuted);
    const canUnmute = Boolean(onUnmuteUser && message.user_id && isChatMuted);
    const canModerate = canMute || canUnmute;
    const muteRevealed = String(revealedMuteId) === String(message.id);
    const content = (
      <div className={`relative flex shrink-0 items-start gap-1.5 animate-in fade-in slide-in-from-bottom-2 duration-300 ${pinned ? "rounded-xl border border-blue-300/45 bg-transparent p-1.5" : ""}`}>
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/25 text-[11px] font-black text-white shadow-md"
          style={{ background: message.color || "linear-gradient(135deg,#3b82f6,#312e81)" }}
        >
          {message.user_picture ? <img src={message.user_picture} alt="" className="h-full w-full rounded-full object-cover" /> : initials(username)}
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex items-center gap-1.5">
            <span className="max-w-[11rem] truncate text-[11px] font-bold text-gray-300">{username}</span>
            {pinned && <Pin className="h-3 w-3 fill-blue-300 text-blue-300" aria-label="Pinned comment" />}
            {!connected && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-yellow-400" />}
            {audience === "creator" && onPinMessage && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onPinMessage(message);
                }}
                className={`pointer-events-auto ml-auto inline-flex min-h-6 touch-manipulation items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-extrabold transition ${pinned ? "border-blue-300/40 bg-blue-500/20 text-blue-100 hover:bg-blue-500/30" : "border-white/15 bg-black/35 text-gray-200 hover:border-blue-300/40 hover:bg-blue-500/20 hover:text-blue-100"}`}
                aria-label={`${pinned ? "Unpin" : "Pin"} comment from ${username}`}
                data-testid={`creator-pin-message-${message.id}`}
              >
                <Pin className={`h-3 w-3 ${pinned ? "fill-blue-200" : ""}`} />
                <span>{pinned ? "Unpin" : "Pin"}</span>
              </button>
            )}
          </div>
          <p className="break-words text-[15px] font-bold leading-[1.2] text-white [text-shadow:0_1px_4px_rgba(0,0,0,.9)]">{body}</p>
        </div>
      </div>
    );

    if (audience !== "creator" || !canModerate) return <React.Fragment key={message.id}>{content}</React.Fragment>;

    return (
      <SwipeChatModerationRow
        key={message.id}
        messageId={message.id}
        username={username}
        isMuted={isChatMuted}
        canModerate={canModerate}
        isRevealed={muteRevealed}
        onReveal={setRevealedMuteId}
        onClose={() => setRevealedMuteId(null)}
        onModerate={() => {
          if (isChatMuted) onUnmuteUser(message);
          else onMuteUser(message);
        }}
      >
        {content}
      </SwipeChatModerationRow>
    );
  };

  return (
    <section
      onClick={isShowMeCongregation ? undefined : keepActive}
      onPointerDown={isShowMeCongregation ? undefined : keepActive}
      className={`absolute bottom-[4.75rem] left-3 z-40 flex ${aboveChatControl ? "max-h-[min(34dvh,15rem)]" : "max-h-[min(25dvh,11rem)]"} w-[min(58vw,23rem)] flex-col overflow-hidden transition-all duration-1000 ease-out sm:left-4 ${
        aboveChatControl
          ? "pointer-events-auto translate-y-0 opacity-100 blur-0"
          : isIdle && isShowMeCongregation
            ? "pointer-events-none translate-y-2 opacity-0 blur-[1px]"
            : isIdle
              ? "pointer-events-auto translate-y-2 opacity-[0.08] blur-[1px]"
              : "pointer-events-auto translate-y-0 opacity-100 blur-0"
      }`}
      data-testid={`${audience}-live-chat-lane`}
      data-idle={isIdle ? "true" : "false"}
      aria-hidden={!aboveChatControl && isIdle && isShowMeCongregation ? "true" : undefined}
      aria-label="Live conversation. Tap the screen to show or keep visible."
    >
      {aboveChatControl && (
        <div className="pointer-events-auto relative z-20 shrink-0 pb-1.5" data-testid={`${audience}-control-above-chat-history`}>
          {aboveChatControl}
        </div>
      )}
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-hidden transition-all duration-1000 ease-out ${
          aboveChatControl && isIdle && isShowMeCongregation
            ? "pointer-events-none translate-y-2 opacity-0 blur-[1px]"
            : aboveChatControl && isIdle
              ? "pointer-events-auto translate-y-2 opacity-[0.08] blur-[1px]"
              : "pointer-events-auto translate-y-0 opacity-100 blur-0"
        }`}
        data-testid={`${audience}-live-chat-content`}
        aria-hidden={aboveChatControl && isIdle && isShowMeCongregation ? "true" : undefined}
      >
        {conversationLabel && !["showmefans", "showmecongregation"].includes(
          String(conversationLabel).trim().toLowerCase(),
        ) && (
          <div className="mb-1 w-fit rounded-full border border-blue-300/25 bg-blue-500/15 px-2.5 py-0.5 text-[10px] font-black text-blue-200">
            {conversationLabel}
          </div>
        )}
        {pinnedMessage && <div className="shrink-0 pb-2">{renderMessage(pinnedMessage, true)}</div>}
        <div
          ref={historyRef}
          onScroll={handleHistoryScroll}
          onWheel={keepActive}
          className="pointer-events-auto relative z-10 flex min-h-0 max-h-[min(22dvh,9.5rem)] flex-1 touch-pan-y flex-col gap-1 overflow-x-hidden overflow-y-auto overscroll-y-contain pb-1 pr-1 [scrollbar-color:rgba(255,255,255,.35)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar]:w-1.5"
          style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
          tabIndex={0}
          role="log"
          aria-live="polite"
          aria-label="Scrollable live messages"
          data-testid={`${audience}-live-chat-history`}
        >
          {historyMessages.map((message) => renderMessage(message))}
        </div>
      </div>
    </section>
  );
};

export const ViewerAudienceSpeakerControl = ({
  educationSpeaker = null,
  speakerMode = "education",
  onRaiseHand,
  onCancelHand,
  onToggleSpeakerMic,
  speakerBusy = false,
}) => {
  if (!educationSpeaker) return null;

  const speakerStatus = educationSpeaker.status || "none";
  const handWaiting = speakerStatus === "waiting";
  const speakerInvited = ["invited", "speaking", "muted"].includes(speakerStatus);
  const speakerMicOn = Boolean(educationSpeaker.mic_enabled);
  const isPodcastCallIn = speakerMode === "podcast";
  const isReligionMic = speakerMode === "religion";
  const speakerIcon = audienceSpeakerIcon(speakerMode);
  const requestAction = isPodcastCallIn ? "Call-In" : isReligionMic ? "Request mic" : "Raise hand to speak";
  const cancelAction = isPodcastCallIn ? "Cancel Call-In" : isReligionMic ? "Cancel mic request" : "Lower hand";
  const speakerTestPrefix = isPodcastCallIn ? "podcast-call-in" : isReligionMic ? "religion-mic" : "education-speaker";
  const leaveSpeakerAction = isPodcastCallIn ? "Leave Call-In" : isReligionMic ? "Leave mic" : "Leave speaker seat";

  if (!speakerInvited) {
    return (
      <button
        type="button"
        onClick={handWaiting ? onCancelHand : onRaiseHand}
        disabled={speakerBusy}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl transition active:scale-95 sm:h-11 sm:w-11 ${glass} ${
          handWaiting ? "border-amber-300/60 bg-amber-400/25" : ""
        }`}
        aria-label={handWaiting ? cancelAction : requestAction}
        title={handWaiting ? cancelAction : requestAction}
        data-testid={isPodcastCallIn ? "podcast-call-in-btn" : isReligionMic ? "religion-mic-btn" : "education-raise-hand-btn"}
      >
        <span aria-hidden="true">{speakerIcon}</span>
      </button>
    );
  }

  return (
    <div className={`relative flex w-fit shrink-0 items-center gap-1 rounded-full p-0.5 sm:p-1 ${glass}`}>
      <span
        className={`pointer-events-none absolute bottom-[calc(100%+0.35rem)] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/75 px-2 py-0.5 text-[10px] font-bold ${
          speakerStatus === "muted" ? "text-red-200" : speakerMicOn ? "text-emerald-200" : "text-gray-200"
        }`}
        data-testid={`${speakerTestPrefix}-mic-status`}
      >
        {speakerStatus === "muted" ? "Creator muted" : speakerMicOn ? "Mic on" : "Mic off"}
      </span>
      <button
        type="button"
        onClick={onToggleSpeakerMic}
        disabled={speakerBusy || speakerStatus === "muted"}
        className={`flex h-9 w-9 items-center justify-center rounded-full transition active:scale-95 ${
          speakerMicOn ? "bg-red-600 text-white" : "bg-emerald-500/20 text-emerald-200"
        } disabled:cursor-not-allowed disabled:opacity-50`}
        aria-label={speakerMicOn ? "Mic off" : "Turn mic on"}
        title={speakerStatus === "muted" ? "Muted by creator" : speakerMicOn ? "Mic off" : "Turn mic on"}
        data-testid={`${speakerTestPrefix}-mic-btn`}
      >
        {speakerMicOn ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={onCancelHand}
        disabled={speakerBusy}
        className="flex h-9 w-9 items-center justify-center rounded-full text-gray-200 transition hover:bg-white/10 active:scale-95 disabled:opacity-50"
        aria-label={leaveSpeakerAction}
        title={leaveSpeakerAction}
        data-testid={`${speakerTestPrefix}-leave-btn`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const ViewerInteractionDock = ({
  chatEnabled,
  reactionsEnabled,
  chatMessage,
  setChatMessage,
  onSendChat,
  onSendReaction,
  onOpenTithe,
  onShare,
  chatConnected,
  reactionProgression,
  isReligion,
  reactionLabel,
  chatPlaceholder,
  tipsEnabled = true,
  tipLabel = "Tithe",
  educationSpeaker = null,
  speakerMode = "education",
  onRaiseHand,
  onCancelHand,
  onToggleSpeakerMic,
  speakerBusy = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const reactionTypes = ["heart", "laugh", "fire", "clap"];

  const sendReaction = (type) => {
    const emoji = reactionProgression.handleReactionTap(type);
    onSendReaction(emoji, type);
  };
  const compactEducationControls = Boolean(educationSpeaker);

  return (
    <div className="absolute inset-x-0 bottom-0 z-50 px-2 pb-[max(env(safe-area-inset-bottom),0.75rem)] sm:px-4" data-testid="viewer-live-controls">
      <div className="mx-auto flex max-w-3xl items-center gap-1.5 sm:gap-2">
        {chatEnabled && (
          <div
            className={`flex min-w-0 flex-1 items-center rounded-full ${glass} ${chatConnected ? "border-white/15" : "border-yellow-400/30"} ${
              compactEducationControls
                ? "h-10 gap-1 px-2 sm:h-11 sm:gap-2 sm:px-4"
                : "h-11 gap-2 px-3 sm:px-4"
            }`}
            data-testid="viewer-chat-bar"
          >
            <MessageCircle className={`${compactEducationControls ? "hidden sm:block" : "block"} h-4 w-4 shrink-0 text-gray-300`} />
            <input
              value={chatMessage}
              onChange={(event) => setChatMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") onSendChat();
              }}
              placeholder={compactEducationControls ? "Message…" : chatPlaceholder || (isReligion ? "Share with the congregation…" : "Add comment…")}
              className={`${compactEducationControls ? "text-xs sm:text-sm" : "text-sm"} w-0 min-w-0 flex-1 bg-transparent font-medium text-white outline-none placeholder:text-gray-400`}
              data-testid="chat-input"
            />
            {chatMessage.trim() && (
              <button type="button" onClick={onSendChat} className="shrink-0 text-sm font-extrabold text-rose-400" data-testid="send-chat-btn">
                send
              </button>
            )}
          </div>
        )}

        {reactionsEnabled && (
          <div className="relative shrink-0">
            {pickerOpen && (
              <div className={`absolute bottom-[calc(100%+0.55rem)] right-0 flex items-center gap-1 rounded-full p-1.5 ${glass}`} aria-label={reactionLabel}>
                {reactionTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => sendReaction(type)}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-xl transition hover:bg-white/10 active:scale-90"
                    data-testid={`reaction-btn-${type}`}
                  >
                    {reactionProgression.getCurrentEmoji(type)}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => setPickerOpen((open) => !open)}
              className={`flex ${compactEducationControls ? "h-10 w-10 sm:h-11 sm:w-11" : "h-11 w-11"} items-center justify-center rounded-full text-yellow-300 transition active:scale-95 ${glass} ${pickerOpen ? "bg-yellow-400/20" : ""}`}
              aria-label={reactionLabel}
              aria-expanded={pickerOpen}
              data-testid="reaction-picker-toggle"
            >
              <Smile className="h-5 w-5" />
            </button>
          </div>
        )}

        {tipsEnabled && (
          <button
            type="button"
            onClick={onOpenTithe}
            className={`flex ${compactEducationControls ? "h-10 w-10 sm:h-11 sm:w-11" : "h-11 w-11"} shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-950/40 transition active:scale-95`}
            aria-label={`Send a ${tipLabel.toLowerCase()}`}
            title={`Send a ${tipLabel.toLowerCase()}`}
            data-testid="tithe-btn"
          >
            <Coins className="h-5 w-5" />
          </button>
        )}

        <ViewerAudienceSpeakerControl
          educationSpeaker={educationSpeaker}
          speakerMode={speakerMode}
          onRaiseHand={onRaiseHand}
          onCancelHand={onCancelHand}
          onToggleSpeakerMic={onToggleSpeakerMic}
          speakerBusy={speakerBusy}
        />

        <button
          type="button"
          onClick={onShare}
          className={`flex ${compactEducationControls ? "h-10 w-10 sm:h-11 sm:w-11" : "h-11 w-11"} shrink-0 items-center justify-center rounded-full text-gray-100 transition active:scale-95 ${glass}`}
          aria-label="Share live event"
          data-testid="viewer-share-btn"
        >
          <Share2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export const LiveShareSheet = ({ isOpen, onClose, eventTitle, eventUrl, isCreator = false, onReport }) => {
  if (!isOpen) return null;

  const shareText = `Watch ${eventTitle || "this event"} live on ShowMeLive`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      toast.success("Live event link copied");
      onClose();
    } catch {
      toast.error("Unable to copy the link");
    }
  };
  const nativeShare = async () => {
    if (!navigator.share) return copyLink();
    try {
      await navigator.share({ title: eventTitle, text: shareText, url: eventUrl });
      onClose();
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("Unable to open sharing");
    }
  };

  const actions = [
    { label: "Copy", icon: Copy, onClick: copyLink, className: "bg-gray-700" },
    { label: "SMS", icon: MessageCircle, href: `sms:?&body=${encodeURIComponent(`${shareText} ${eventUrl}`)}`, className: "bg-green-600" },
    { label: "Email", icon: Mail, href: `mailto:?subject=${encodeURIComponent(eventTitle || "ShowMeLive")}&body=${encodeURIComponent(`${shareText}\n${eventUrl}`)}`, className: "bg-blue-600" },
    { label: "More", icon: Share2, onClick: nativeShare, className: "bg-violet-600" },
  ];

  return (
    <div className="absolute inset-0 z-[80]" data-testid="live-share-sheet">
      <button type="button" className="absolute inset-0 h-full w-full bg-black/50 backdrop-blur-sm" onClick={onClose} aria-label="Close share sheet" />
      <section className="absolute inset-x-0 bottom-0 rounded-t-[1.65rem] border-t border-white/15 bg-[#0e0e11]/95 pb-[max(env(safe-area-inset-bottom),1.5rem)] shadow-2xl backdrop-blur-2xl animate-in slide-in-from-bottom duration-300">
        <div className="flex justify-center py-3"><span className="h-1.5 w-11 rounded-full bg-gray-700" /></div>
        <div className="flex items-center justify-between px-5 pb-4">
          <h2 className="text-lg font-extrabold text-white">{isCreator ? "Invite people to your live" : "Share this live"}</h2>
          <button type="button" onClick={onClose} className="rounded-full bg-white/5 p-2 text-gray-300"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-4 gap-3 px-5">
          {actions.map(({ label, icon: Icon, href, onClick, className }) => {
            const content = (
              <>
                <span className={`flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg ${className}`}><Icon className="h-6 w-6" /></span>
                <span className="mt-2 text-xs font-medium text-gray-300">{label}</span>
              </>
            );
            return href ? (
              <a key={label} href={href} className="flex flex-col items-center">{content}</a>
            ) : (
              <button key={label} type="button" onClick={onClick} className="flex flex-col items-center">{content}</button>
            );
          })}
        </div>
        {onReport && !isCreator && (
          <button
            type="button"
            onClick={() => { onClose(); onReport(); }}
            className="mx-auto mt-6 flex items-center gap-2 text-xs font-semibold text-red-300"
          >
            <Flag className="h-4 w-4" /> Report this stream
          </button>
        )}
      </section>
    </div>
  );
};
